import { Dispatch } from "redux";
import { createSlice } from "@reduxjs/toolkit";
import { getContractAllocationApi } from "../../_apis/ContractCrud";
import Big from "big.js";
import { AbiItem } from "web3-utils";
import ApproveRouterJson from "../../utils/abi/ApproveRouterAbi.json";

export interface CONTRACT_INITIAL_STATE {
  error: boolean;
  isLoading: boolean;
  allocation: string;
  contractAddress: string;
  isApproved: boolean;
  isApproving: boolean;
  approvalError: boolean;
  approvalErrorMessage: string;
  approvalTransactionHash: string;
}

const initialState: CONTRACT_INITIAL_STATE = {
  error: false,
  isLoading: false,
  allocation: "",
  contractAddress: "",
  isApproved: false,
  isApproving: false,
  approvalError: false,
  approvalErrorMessage: "",
  approvalTransactionHash: "",
};

const slice = createSlice({
  name: "contract",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    startApproving(state) {
      state.isLoading = true;
      state.isApproved = false;
      state.isApproving = true;
      state.contractAddress = "";
      state.approvalError = false;
      state.approvalErrorMessage = "";
      state.approvalTransactionHash = "";
    },

    hasError(state, action) {
      state.isLoading = false;
      state.isApproved = false;
      state.isApproving = false;
      state.error = action.payload;
    },

    getContractAllocationPayload(state, action) {
      state.isLoading = false;
      state.isApproved = action.payload.isApproved;
      state.isApproving = false;
      state.allocation = action.payload.allocation;
      state.contractAddress = "";
    },

    setApproveTransactionHash(state, action) {
      state.isApproved = true;
      state.isApproving = false;
      state.approvalTransactionHash = action.payload;
    },

    resetApproveTxAndError(state) {
      state.approvalTransactionHash = "";
      state.approvalError = false;
      state.approvalErrorMessage = "";
    },

    setApprovalError(state, action) {
      state.isApproved = false;
      state.isApproving = false;
      state.approvalError = action.payload.approvalError;
      state.approvalErrorMessage = action.payload.approvalErrorMessage;
    },

    resetAllocationState(state) {
      state.isLoading = false;
      state.isApproved = false;
      state.isApproving = false;
      state.allocation = "";
      state.contractAddress = "";
      state.approvalError = false;
      state.approvalErrorMessage = "";
      state.approvalTransactionHash = "";
    },
  },
});

export default slice.reducer;

export const getContractAllocation =
  (sourceCabnId: string, sourceNetworkId: string, authToken: string | null) =>
  (dispatch: Dispatch) => {
    getContractAllocationApi(sourceCabnId, sourceNetworkId, authToken)
      .then((res) => {
        let { allocation, contractAddress } = res.data.body.data;
        dispatch(
          slice.actions.getContractAllocationPayload({
            allocation,
            contractAddress,
          })
        );
      })
      .catch((e) => {
        if (e.response) {
          dispatch(slice.actions.hasError(e.response?.data?.status?.message));
        } else {
          dispatch(
            slice.actions.hasError("Something went wrong. Try again later!")
          );
        }
      });
  };

export const getStandardContractAllocation =
  (
    tokenContractAddress: string,
    walletAddress: string,
    fiberRouterSmartContractAddress: string | null,
    web3Sdk: any,
    amount: number
  ) =>
  async (dispatch: Dispatch) => {
    if (web3Sdk && tokenContractAddress) {
      const contractInstance = new web3Sdk.eth.Contract(
        ApproveRouterJson.abi as AbiItem[],
        tokenContractAddress
      );
      const response = await contractInstance.methods
        .allowance(walletAddress, fiberRouterSmartContractAddress)
        .call();
      if (response) {
        const decimals = await contractInstance.methods.decimals().call();
        const decimalFactor = 10 ** decimals;
        const allowance = new Big(response).div(decimalFactor).toFixed();

        if (allowance && Number(allowance) >= amount) {
          dispatch(
            slice.actions.getContractAllocationPayload({
              isApproved: true,
              allocation: allowance,
            })
          );
          return true;
        } else {
          dispatch(
            slice.actions.getContractAllocationPayload({
              isApproved: false,
              allocation: allowance,
            })
          );
          return false;
        }
      }
    }
  };

export const approveContractAllocation =
  (
    sourceChainId: string,
    tokenContractAddress: string,
    walletAddress: string,
    fiberRouterSmartContractAddress: string,
    web3Sdk: any,
    amount: number,
    sendTransaction: any
  ) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(slice.actions.startApproving());
      const contractInstance = new web3Sdk.eth.Contract(
        ApproveRouterJson.abi as AbiItem[],
        tokenContractAddress
      );
      const decimals = await contractInstance.methods.decimals().call();
      const payload = {
        from: walletAddress,
        to: tokenContractAddress,
        data: contractInstance.methods
          .approve(
            fiberRouterSmartContractAddress,
            new Big(amount)
              .times(10 ** decimals)
              .toFixed()
              .toString()
          )
          .encodeABI(),
      };
      return sendTransaction(payload);
    } catch (e: any) {
      console.log(e);
      console.log(JSON.stringify(e?.message));
      let error = e?.message || e;
      console.log(error);

      if (JSON.stringify(error)?.includes("reverted by the EVM")) {
        error = "Transaction has been reverted by the EVM";
      } else if (JSON.stringify(error).includes("rpc error")) {
        console.log(error.split(","));
        error =
          error.split(",")[6].split(":")[1].replace("}", "").trim() ||
          "Something went wrong";
      }
      dispatch(
        slice.actions.setApprovalError({
          approvalError: true,
          approvalErrorMessage: error,
        })
      );
      console.log(error);
    }
  };

export const resetApproveTxAndError = () => (dispatch: Dispatch) => {
  dispatch(slice.actions.resetApproveTxAndError());
};

export const resetContractAllocationState = () => (dispatch: Dispatch) => {
  dispatch(slice.actions.resetAllocationState());
};
